import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Card,
  Container,
  HeaderBar,
  Loader,
  Toast as toast,
  TypoGraph,
} from "components";
import {
  createSftpReportConfig,
  getSftpReportConfig,
  updateSftpReportConfig,
} from "api";
import {
  ISftpReportConfigRequest,
  ISftpReportConfigResponse,
} from "interfaces";
import styles from "./reportIntegrations.module.scss";

const REPORT_TYPES = ["LOSS_DRAFT"];
const DATE_WINDOW_OPTIONS = ["CREATED", "PROCESSED", "DATE_OF_LOSS"];
const QUOTE_MODES = ["MINIMAL", "ALL", "NON_NUMERIC", "NONE"];
const LINE_ENDINGS = ["LF", "CRLF"];
const AUTH_TYPES = ["PASSWORD", "KEY"];

const emptyForm: ISftpReportConfigRequest = {
  companySlug: "",
  displayName: "",
  reportType: "LOSS_DRAFT",
  brokerCompanyIdsCsv: "",
  enabled: false,
  cronExpression: "0 15 0 * * *",
  cronTimezone: "America/New_York",
  dateWindowBasis: "CREATED",
  csvDelimiter: "|",
  csvQuoteMode: "MINIMAL",
  csvIncludeHeader: true,
  csvLineEnding: "LF",
  csvCharset: "UTF-8",
  csvBom: false,
  csvSkipEmpty: false,
  filenamePattern: "{yyyyMMdd_HHmmss}.csv",
  filenameTimezone: "America/New_York",
  sftpHost: "",
  sftpPort: 22,
  sftpUsername: "",
  sftpCredential: "",
  sftpAuthType: "PASSWORD",
  sftpKnownHostKey: "",
  sftpRemoteDirectory: "/",
  archiveToS3: true,
};

const ReportIntegrationFormPage = () => {
  const navigate = useNavigate();
  const params = useParams<{ id?: string }>();
  const id = params.id && params.id !== "new" ? Number(params.id) : null;
  const isEdit = id != null;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<ISftpReportConfigRequest>(emptyForm);
  const [existingMasked, setExistingMasked] = useState<string | null>(null);

  useEffect(() => {
    if (!isEdit) {
      return;
    }
    const load = async () => {
      try {
        const res = await getSftpReportConfig(id!);
        applyExisting(res.data);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, isEdit]);

  const applyExisting = (cfg: ISftpReportConfigResponse) => {
    setExistingMasked(cfg.sftpCredentialMasked);
    setForm({
      companySlug: cfg.companySlug,
      displayName: cfg.displayName,
      reportType: cfg.reportType,
      brokerCompanyIdsCsv: cfg.brokerCompanyIdsCsv,
      enabled: cfg.enabled,
      cronExpression: cfg.cronExpression,
      cronTimezone: cfg.cronTimezone,
      dateWindowBasis: cfg.dateWindowBasis,
      csvDelimiter: cfg.csvDelimiter,
      csvQuoteMode: cfg.csvQuoteMode,
      csvIncludeHeader: cfg.csvIncludeHeader,
      csvLineEnding: cfg.csvLineEnding,
      csvCharset: cfg.csvCharset,
      csvBom: cfg.csvBom,
      csvSkipEmpty: cfg.csvSkipEmpty,
      filenamePattern: cfg.filenamePattern,
      filenameTimezone: cfg.filenameTimezone,
      sftpHost: cfg.sftpHost,
      sftpPort: cfg.sftpPort,
      sftpUsername: cfg.sftpUsername,
      sftpCredential: "",
      sftpAuthType: cfg.sftpAuthType,
      sftpKnownHostKey: cfg.sftpKnownHostKey,
      sftpRemoteDirectory: cfg.sftpRemoteDirectory,
      archiveToS3: cfg.archiveToS3,
    });
  };

  const updateField = <K extends keyof ISftpReportConfigRequest>(
    key: K,
    value: ISftpReportConfigRequest[K]
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    setError(null);
    setSaving(true);
    try {
      if (isEdit) {
        await updateSftpReportConfig(id!, form);
        toast({ title: "Saved", subTitle: "Integration updated" });
      } else {
        await createSftpReportConfig(form);
        toast({ title: "Created", subTitle: "Integration created" });
      }
      navigate("/settings/report-integrations");
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Failed to save integration";
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container className={styles.container}>
        <HeaderBar title={isEdit ? "Edit integration" : "New integration"} />
        <Loader loading={true} />
      </Container>
    );
  }

  const credentialPlaceholder = isEdit && existingMasked
    ? "•••••••• (unchanged — leave blank to keep)"
    : "Required";

  return (
    <Container className={styles.container}>
      <HeaderBar title={isEdit ? "Edit integration" : "New integration"} />

      <Container className={styles.formContainer}>
        <Card sxContent={{ display: "grid", gridGap: "16px", padding: "16px" }}>
          {error && <Container className={styles.errorBanner}>{error}</Container>}

          <TypoGraph variant="h3" content="Identity" />
          <Container className={styles.formGrid}>
            <label className={styles.label}>
              <TypoGraph variant="body2" content="Company slug" />
              <input
                className={styles.input}
                value={form.companySlug}
                onChange={(e) => updateField("companySlug", e.target.value)}
                placeholder="cornerstone"
                disabled={isEdit}
              />
            </label>
            <label className={styles.label}>
              <TypoGraph variant="body2" content="Display name" />
              <input
                className={styles.input}
                value={form.displayName}
                onChange={(e) => updateField("displayName", e.target.value)}
                placeholder="Cornerstone Lending"
              />
            </label>
            <label className={styles.label}>
              <TypoGraph variant="body2" content="Report type" />
              <select
                className={styles.select}
                value={form.reportType}
                onChange={(e) => updateField("reportType", e.target.value)}
              >
                {REPORT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.label}>
              <TypoGraph variant="body2" content="Broker company IDs (comma-separated)" />
              <input
                className={styles.input}
                value={form.brokerCompanyIdsCsv}
                onChange={(e) =>
                  updateField("brokerCompanyIdsCsv", e.target.value)
                }
                placeholder="100,101"
              />
            </label>
            <label className={`${styles.checkboxRow} ${styles.formGridFull}`}>
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => updateField("enabled", e.target.checked)}
              />
              <TypoGraph
                variant="body2"
                content="Enabled (dispatcher will pick up on its next tick)"
              />
            </label>
          </Container>

          <TypoGraph variant="h3" content="Schedule" />
          <Container className={styles.formGrid}>
            <label className={styles.label}>
              <TypoGraph variant="body2" content="Cron expression (Spring 6-field)" />
              <input
                className={styles.input}
                value={form.cronExpression}
                onChange={(e) => updateField("cronExpression", e.target.value)}
                placeholder="0 15 0 * * *"
              />
            </label>
            <label className={styles.label}>
              <TypoGraph variant="body2" content="Cron timezone" />
              <input
                className={styles.input}
                value={form.cronTimezone}
                onChange={(e) => updateField("cronTimezone", e.target.value)}
                placeholder="America/New_York"
              />
            </label>
            <label className={styles.label}>
              <TypoGraph variant="body2" content="Date window basis" />
              <select
                className={styles.select}
                value={form.dateWindowBasis}
                onChange={(e) => updateField("dateWindowBasis", e.target.value)}
              >
                {DATE_WINDOW_OPTIONS.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.label}>
              <TypoGraph variant="body2" content="Filename timezone" />
              <input
                className={styles.input}
                value={form.filenameTimezone}
                onChange={(e) =>
                  updateField("filenameTimezone", e.target.value)
                }
              />
            </label>
            <label className={`${styles.label} ${styles.formGridFull}`}>
              <TypoGraph variant="body2" content="Filename pattern (supports {yyyyMMdd_HHmmss} tokens)" />
              <input
                className={styles.input}
                value={form.filenamePattern}
                onChange={(e) => updateField("filenamePattern", e.target.value)}
                placeholder="cornerstone_loss_draft_{yyyyMMdd_HHmmss}.csv"
              />
            </label>
          </Container>

          <TypoGraph variant="h3" content="CSV dialect" />
          <Container className={styles.formGrid}>
            <label className={styles.label}>
              <TypoGraph variant="body2" content="Delimiter (single char)" />
              <input
                className={styles.input}
                maxLength={1}
                value={form.csvDelimiter}
                onChange={(e) =>
                  updateField("csvDelimiter", e.target.value || "|")
                }
              />
            </label>
            <label className={styles.label}>
              <TypoGraph variant="body2" content="Quote mode" />
              <select
                className={styles.select}
                value={form.csvQuoteMode}
                onChange={(e) => updateField("csvQuoteMode", e.target.value)}
              >
                {QUOTE_MODES.map((q) => (
                  <option key={q} value={q}>
                    {q}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.label}>
              <TypoGraph variant="body2" content="Line ending" />
              <select
                className={styles.select}
                value={form.csvLineEnding}
                onChange={(e) => updateField("csvLineEnding", e.target.value)}
              >
                {LINE_ENDINGS.map((l) => (
                  <option key={l} value={l}>
                    {l}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.label}>
              <TypoGraph variant="body2" content="Charset" />
              <input
                className={styles.input}
                value={form.csvCharset}
                onChange={(e) => updateField("csvCharset", e.target.value)}
              />
            </label>
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={form.csvIncludeHeader}
                onChange={(e) =>
                  updateField("csvIncludeHeader", e.target.checked)
                }
              />
              <TypoGraph variant="body2" content="Include header row" />
            </label>
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={form.csvBom}
                onChange={(e) => updateField("csvBom", e.target.checked)}
              />
              <TypoGraph variant="body2" content="UTF-8 BOM" />
            </label>
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={form.csvSkipEmpty}
                onChange={(e) =>
                  updateField("csvSkipEmpty", e.target.checked)
                }
              />
              <TypoGraph variant="body2" content="Skip upload when window has zero rows" />
            </label>
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={form.archiveToS3}
                onChange={(e) => updateField("archiveToS3", e.target.checked)}
              />
              <TypoGraph variant="body2" content="Archive each file to S3" />
            </label>
          </Container>

          <TypoGraph variant="h3" content="SFTP destination" />
          <Container className={styles.formGrid}>
            <label className={styles.label}>
              <TypoGraph variant="body2" content="Host" />
              <input
                className={styles.input}
                value={form.sftpHost}
                onChange={(e) => updateField("sftpHost", e.target.value)}
              />
            </label>
            <label className={styles.label}>
              <TypoGraph variant="body2" content="Port" />
              <input
                className={styles.input}
                type="number"
                value={form.sftpPort}
                onChange={(e) =>
                  updateField("sftpPort", Number(e.target.value) || 22)
                }
              />
            </label>
            <label className={styles.label}>
              <TypoGraph variant="body2" content="Username" />
              <input
                className={styles.input}
                value={form.sftpUsername}
                onChange={(e) => updateField("sftpUsername", e.target.value)}
              />
            </label>
            <label className={styles.label}>
              <TypoGraph variant="body2" content="Remote directory" />
              <input
                className={styles.input}
                value={form.sftpRemoteDirectory}
                onChange={(e) =>
                  updateField("sftpRemoteDirectory", e.target.value)
                }
              />
            </label>
            <label className={styles.label}>
              <TypoGraph variant="body2" content="Auth type" />
              <select
                className={styles.select}
                value={form.sftpAuthType}
                onChange={(e) => updateField("sftpAuthType", e.target.value)}
              >
                {AUTH_TYPES.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.label}>
              <TypoGraph
                variant="body2"
                content={
                  form.sftpAuthType === "KEY"
                    ? "Private key (PEM)"
                    : "Password"
                }
              />
              <input
                className={styles.input}
                type="password"
                autoComplete="new-password"
                value={form.sftpCredential}
                onChange={(e) => updateField("sftpCredential", e.target.value)}
                placeholder={credentialPlaceholder}
              />
            </label>
            <label className={`${styles.label} ${styles.formGridFull}`}>
              <TypoGraph
                variant="body2"
                content="Pinned known host key (e.g. 'ssh-rsa AAAA…')"
              />
              <textarea
                className={styles.textarea}
                value={form.sftpKnownHostKey}
                onChange={(e) =>
                  updateField("sftpKnownHostKey", e.target.value)
                }
              />
            </label>
          </Container>

          <Container className={styles.formActions}>
            <Button
              id="saveReportIntegration"
              label={isEdit ? "Save changes" : "Create"}
              variant="contained"
              loading={saving}
              disabled={!form.companySlug || !form.displayName}
              onClick={handleSubmit}
            />
            <Button
              id="cancelReportIntegration"
              label="Cancel"
              variant="outlined"
              onClick={() => navigate("/settings/report-integrations")}
            />
          </Container>
        </Card>
      </Container>
    </Container>
  );
};

export default ReportIntegrationFormPage;
